import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Link2,
  Copy,
  Check,
  Plus,
  ExternalLink,
  MousePointerClick,
  ShoppingCart,
  DollarSign,
  X,
} from 'lucide-react';
import {
  useTikTokFetch,
  Product,
  formatCurrency,
  formatNumber,
  formatDate,
} from '../hooks/useTikTokFetch';
import '../styles/pages.css';

interface AffiliateLink {
  id: string;
  affiliate_url: string;
  short_url: string | null;
  tracking_code: string;
  destination_url: string;
  product_id: string | null;
  product_title: string | null;
  link_name: string | null;
  clicks: number;
  conversions: number;
  revenue: number;
  created_at: string;
}

interface GeneratedLink {
  id: string;
  affiliate_url: string;
  short_url: string;
  tracking_code: string;
  product?: {
    id: string;
    title: string;
  };
}

export default function Links() {
  const { get, post, getShopId } = useTikTokFetch();
  const shopId = getShopId();
  const queryClient = useQueryClient();

  const [showGenerator, setShowGenerator] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [linkName, setLinkName] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [generatedLink, setGeneratedLink] = useState<GeneratedLink | null>(null);
  const [page, setPage] = useState(0);
  const limit = 20;

  // Fetch links
  const { data: links = [], isLoading } = useQuery<AffiliateLink[]>({
    queryKey: ['links', shopId, page],
    queryFn: () => get(`/api/shop/${shopId}/links?limit=${limit}&offset=${page * limit}`),
    enabled: !!shopId,
  });

  // Fetch products for generator dropdown
  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ['products', shopId, 'all'],
    queryFn: () => get(`/api/shop/${shopId}/products?limit=250`),
    enabled: !!shopId && showGenerator,
  });

  // Generate link
  const generateMutation = useMutation({
    mutationFn: (data: {
      product_id?: string;
      destination_url?: string;
      link_name?: string;
    }) => post<GeneratedLink>(`/api/shop/${shopId}/links/generate`, data),
    onSuccess: (data) => {
      setGeneratedLink(data);
      queryClient.invalidateQueries({ queryKey: ['links'] });
    },
  });

  const handleGenerate = useCallback(() => {
    if (selectedProduct) {
      generateMutation.mutate({
        product_id: selectedProduct,
        link_name: linkName || undefined,
      });
    } else if (customUrl) {
      generateMutation.mutate({
        destination_url: customUrl,
        link_name: linkName || undefined,
      });
    }
  }, [selectedProduct, customUrl, linkName, generateMutation]);

  const handleCopy = useCallback(async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback for non-HTTPS
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  }, []);

  const handleReset = useCallback(() => {
    setSelectedProduct('');
    setCustomUrl('');
    setLinkName('');
    setGeneratedLink(null);
  }, []);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Affiliate Links</h1>
        <button
          className="btn btn--primary"
          onClick={() => {
            setShowGenerator(!showGenerator);
            if (showGenerator) handleReset();
          }}
        >
          {showGenerator ? (
            <>
              <X size={14} />
              Close
            </>
          ) : (
            <>
              <Plus size={14} />
              Generate Link
            </>
          )}
        </button>
      </div>

      {/* Link Generator */}
      {showGenerator && (
        <motion.section
          className="card"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="card-header">
            <Link2 size={18} />
            <h2>Generate New Link</h2>
          </div>
          <div className="card-body">
            <div className="link-generator-form">
              {/* Product selector */}
              <div className="form-group">
                <label className="form-label">Select Product</label>
                <select
                  className="input input--select"
                  value={selectedProduct}
                  onChange={(e) => {
                    setSelectedProduct(e.target.value);
                    setCustomUrl('');
                    setGeneratedLink(null);
                  }}
                  disabled={!!customUrl}
                >
                  <option value="">Choose a product...</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-divider">
                <span className="text-muted text-sm">OR</span>
              </div>

              {/* Custom URL */}
              <div className="form-group">
                <label className="form-label">Custom URL</label>
                <input
                  type="url"
                  className="input"
                  placeholder="https://your-tiktok-shop.com/product/..."
                  value={customUrl}
                  onChange={(e) => {
                    setCustomUrl(e.target.value);
                    setSelectedProduct('');
                    setGeneratedLink(null);
                  }}
                  disabled={!!selectedProduct}
                />
              </div>

              {/* Link Name */}
              <div className="form-group">
                <label className="form-label">Link Name (optional)</label>
                <input
                  type="text"
                  className="input"
                  placeholder="e.g., Summer Sale Campaign"
                  value={linkName}
                  onChange={(e) => setLinkName(e.target.value)}
                />
              </div>

              <div className="form-actions">
                <button
                  className="btn btn--primary"
                  onClick={handleGenerate}
                  disabled={(!selectedProduct && !customUrl) || generateMutation.isPending}
                >
                  {generateMutation.isPending ? 'Generating...' : 'Generate Link'}
                </button>
                {(selectedProduct || customUrl || generatedLink) && (
                  <button className="btn btn--secondary" onClick={handleReset}>
                    Reset
                  </button>
                )}
              </div>

              {generateMutation.isError && (
                <div className="billing-banner billing-banner--danger">
                  <span>Failed to generate link. Please try again.</span>
                </div>
              )}
            </div>

            {/* Generated Result */}
            {generatedLink && (
              <motion.div
                className="generated-link-result"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="generated-header">
                  <span className="badge badge--success">Link Ready</span>
                  {generatedLink.product && (
                    <span className="text-secondary text-sm">
                      {generatedLink.product.title}
                    </span>
                  )}
                </div>

                {/* Affiliate URL */}
                <div className="generated-field">
                  <label className="text-muted text-sm">Affiliate URL</label>
                  <div className="copy-row">
                    <input
                      type="text"
                      className="input mono"
                      value={generatedLink.affiliate_url}
                      readOnly
                    />
                    <button
                      className="btn btn--sm"
                      onClick={() => handleCopy(generatedLink.affiliate_url, 'gen-affiliate')}
                    >
                      {copiedId === 'gen-affiliate' ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>

                {/* Short URL */}
                {generatedLink.short_url && (
                  <div className="generated-field">
                    <label className="text-muted text-sm">Short URL</label>
                    <div className="copy-row">
                      <input
                        type="text"
                        className="input mono"
                        value={generatedLink.short_url}
                        readOnly
                      />
                      <button
                        className="btn btn--sm"
                        onClick={() => handleCopy(generatedLink.short_url, 'gen-short')}
                      >
                        {copiedId === 'gen-short' ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    </div>
                  </div>
                )}

                {/* Tracking Code */}
                <div className="generated-field">
                  <label className="text-muted text-sm">Tracking Code</label>
                  <div className="copy-row">
                    <input
                      type="text"
                      className="input mono"
                      value={generatedLink.tracking_code}
                      readOnly
                    />
                    <button
                      className="btn btn--sm"
                      onClick={() => handleCopy(generatedLink.tracking_code, 'gen-tracking')}
                    >
                      {copiedId === 'gen-tracking' ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </motion.section>
      )}

      {/* Links Table */}
      {isLoading ? (
        <div className="loading-spinner" />
      ) : links.length === 0 ? (
        <div className="empty-state">
          <Link2 size={48} className="text-muted" />
          <p>No affiliate links yet</p>
          <p className="text-secondary text-sm">
            Generate your first link to start tracking clicks and conversions.
          </p>
        </div>
      ) : (
        <motion.div className="table-wrap" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Link</th>
                <th>Product</th>
                <th>
                  <MousePointerClick size={12} style={{ display: 'inline', verticalAlign: 'middle' }} />{' '}
                  Clicks
                </th>
                <th>
                  <ShoppingCart size={12} style={{ display: 'inline', verticalAlign: 'middle' }} />{' '}
                  Conversions
                </th>
                <th>
                  <DollarSign size={12} style={{ display: 'inline', verticalAlign: 'middle' }} />{' '}
                  Revenue
                </th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {links.map((link) => (
                <tr key={link.id}>
                  <td>
                    <div className="link-cell">
                      <span className="link-name">
                        {link.link_name || link.short_url || 'Affiliate Link'}
                      </span>
                      <span className="link-url mono text-sm text-muted">
                        {link.short_url || link.affiliate_url}
                      </span>
                    </div>
                  </td>
                  <td>{link.product_title || '---'}</td>
                  <td className="mono">{formatNumber(link.clicks)}</td>
                  <td className="mono">{formatNumber(link.conversions)}</td>
                  <td className="text-accent mono">{formatCurrency(link.revenue)}</td>
                  <td className="text-secondary">{formatDate(link.created_at)}</td>
                  <td>
                    <div className="action-btns">
                      <button
                        className="btn btn--ghost btn--sm"
                        title="Copy link"
                        onClick={() =>
                          handleCopy(link.short_url || link.affiliate_url, link.id)
                        }
                      >
                        {copiedId === link.id ? (
                          <Check size={14} className="text-accent" />
                        ) : (
                          <Copy size={14} />
                        )}
                      </button>
                      <a
                        href={link.affiliate_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn--ghost btn--sm"
                        title="Open link"
                      >
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      )}

      {links.length >= limit && (
        <div className="pagination">
          <button
            className="btn btn--sm"
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
          >
            Previous
          </button>
          <span className="text-secondary">Page {page + 1}</span>
          <button className="btn btn--sm" onClick={() => setPage(page + 1)}>
            Next
          </button>
        </div>
      )}
    </div>
  );
}
